import { FieldCollector, agentJsonReplacer, buildComponentsIndex, filterSnapshotToPaths } from './FieldCollector.js'
import { FieldChangesProposal } from './Assistant.js'
import { gApp } from '../Globals.js'
import { SystemResultsState, SystemResultsOutcome } from '../BuildingComponents/System.js'
import { getSystemResultsCsv } from '../BuildingComponents/SystemResultsExcelExporter.js'
import { searchLocations } from '../Components/LocationData.js'
import { getResidentialResultsCsv } from '../Components/ResidentialResultsCsv.js'
import { ResultsState } from '../Base.js'
import { Field } from '../Common/Field.js'

const SYSTEM_PATH_RE = /^\/systems\[(\d+)\]$/

// Resolves a System instance from a path like '/systems[0]', the same
// addressing scheme used elsewhere (e.g. propose_changes' component_path).
function resolveSystemFromPath(systemPath) {
  const systems = gApp.proj().systems
  if (!systems) {
    throw new Error(NO_SYSTEMS_ERROR)
  }
  const match = SYSTEM_PATH_RE.exec(systemPath)
  if (!match) {
    throw new Error(`Invalid system_path '${systemPath}'. Expected a path like '/systems[0]', as returned by read_project or check_system_calculation_status.`)
  }
  const system = systems[Number(match[1])]
  if (!system) {
    throw new Error(`No system found at path '${systemPath}'.`)
  }
  return system
}

// err.page can carry a live `typeObject: this` reference to the reactive
// component (see e.g. Space.getInputPage()), which loops back through parent
// references into Vue's reactivity internals — not serializable. Keep only
// the plain, useful fields.
function sanitizeProjectErrors(errors) {
  return errors.map(err => ({
    msg: err.msg,
    path: err.path,
    page: err.page ? { label: err.page.label, path: err.page.path, typeName: err.page.typeName } : null,
    childDetails: (err.childDetails ?? []).map(d => ({ path: d.path })),
  }))
}

// Type-library sections shared by both project types (the Walls, Roofs,
// Windows, Doors, Skylights, Shadings and Buffer Spaces tabs).
const SHARED_TYPE_SECTIONS = [
  'wallTypes', 'roofTypes', 'windowTypes', 'doorTypes', 'skylightTypes',
  'interiorShadingTypes', 'exteriorShadingTypes', 'bufferSpaceTypes', 'componentsLibrary',
]

// Top-level sections a project's read_project response can be scoped to
// (the project class's serFields' ComponentArray/object fields), per project
// type. Static and small enough to just list in the tool description — no
// need for the agent to discover these dynamically the way it discovers
// individual component names via the 'components' index.
const COMMERCIAL_SECTIONS = [
  'buildingAndEnv', 'systems', 'spaces', 'schedules', ...SHARED_TYPE_SECTIONS,
]
// A Residential project is a single house: everything on its Build House tab
// lives directly on the project (no Systems/Spaces/Zones/Schedules).
const RESIDENTIAL_SECTIONS = [
  'toplevelData', 'designTempInputs', 'ventilationInfiltration', 'internals', 'miscDetails',
  'walls', 'roofs', 'floors', 'partitions', ...SHARED_TYPE_SECTIONS,
]

function getProjectSections(project) {
  return project.isResidential() ? RESIDENTIAL_SECTIONS : COMMERCIAL_SECTIONS
}

const sectionList = (sections) => sections.map(s => `"/${s}"`).join(', ')

const COMMERCIAL_ONLY_NOTE =
  `Commercial projects only — Residential projects have no Systems; use calculate_house_loads and ` +
  `get_house_results for those instead.`

const RESIDENTIAL_ONLY_NOTE =
  `Residential projects only — for Commercial projects, use calculate_system, ` +
  `check_system_calculation_status and get_system_results instead.`

const NO_SYSTEMS_ERROR =
  'This is a Residential project, which has no Systems (Systems only exist in Commercial projects). ' +
  'Use calculate_house_loads and get_house_results for Residential loads.'

// Outcome of a System's calculation, as reported by calculate_system and
// check_system_calculation_status.
function getSystemStatus(system, systemPath) {
  const resultsState = system.getResultsState()
  const status = {
    system_path: systemPath,
    name: system.getName(),
    resultsState,
  }
  if (resultsState === SystemResultsState.Ready) {
    const outcome = system.loadResults.getOutcome()
    status.resultsOutcome = outcome
    if (outcome === SystemResultsOutcome.FailedWithProjectErrors) {
      status.projectErrors = sanitizeProjectErrors(system.loadResults.getProjectErrors().getErrors())
    } else if (outcome === SystemResultsOutcome.FailedWithCalculationError) {
      status.calculationError = system.loadResults.getCalculationError()
    }
  }
  return status
}

// How long calculate_system waits for a calculation before handing back a
// "still calculating" status. Long enough for any normal project.
const SYSTEM_CALC_MAX_WAIT_MS = 5 * 60 * 1000

async function waitForSystemCalculation(system) {
  const start = Date.now()
  while (system.getResultsState() === SystemResultsState.Calculating) {
    if (Date.now() - start > SYSTEM_CALC_MAX_WAIT_MS) return false
    await new Promise(r => setTimeout(r, 500))
  }
  return true
}

// Shared by both calculation tools: models tend to stop after running a
// calculation unless told to carry on with what the user actually asked for.
const AFTER_CALCULATION_NOTE =
  `If the user asked about the results (e.g. "run the calculations and summarize them"), read them ` +
  `with the results tool and answer in the same reply. Don't stop after the calculation finishes.`

function requireResidentialProject() {
  const project = gApp.proj()
  if (!project.isResidential()) {
    throw new Error('This is a Commercial project. Use calculate_system, check_system_calculation_status and get_system_results for Commercial loads.')
  }
  return project
}

// Outcome of a Residential project's last calculation, shaped like
// check_system_calculation_status's per-System entries.
function getHouseResultsStatus(project) {
  const status = { resultsState: project.getResultsState() }
  if (project.getResultsState() !== ResultsState.Ready) return status
  const results = project.getResults()
  if (results.getProjectErrors()) {
    status.resultsOutcome = 'FailedWithProjectErrors'
    status.projectErrors = sanitizeProjectErrors(results.getProjectErrors().getErrors())
  } else if (results.getError()) {
    status.resultsOutcome = 'FailedWithCalculationError'
    status.calculationError = String(results.getError()?.message ?? results.getError())
  } else {
    status.resultsOutcome = 'Success'
  }
  return status
}

// Client-side tools. Each entry is sent to the server on connection so it can
// register the tool with the AI. When the AI calls the tool, the server sends a
// tool_call message; handle() runs the client-side logic and returns the tool
// result string. The result is sent back to the server immediately as the
// client_response, so the assistant runner never blocks on user interaction.
export const CLIENT_TOOLS = [
  {
    name: 'read_project',
    description:
      `Read the current project state — input fields and their values. Always includes 'projectType' ` +
      `('Commercial' or 'Residential'), 'sections' (the top-level section names this project has), and a ` +
      `'components' index: {path, name, componentType} for every item in the project's top-level ` +
      `ComponentArray sections, e.g. {"path": "/systems[0]", "name": "System-1", "componentType": "System"}. ` +
      `Items without a name field (e.g. a Residential project's walls) are listed by position, e.g. "Wall 2". ` +
      `The two project types have different top-level sections:\n` +
      `- Commercial: ${sectionList(COMMERCIAL_SECTIONS)}.\n` +
      `- Residential: ${sectionList(RESIDENTIAL_SECTIONS)}. The Build House tab's General/location ` +
      `inputs are in toplevelData, and its Ventilation/Infiltration, Internal Gains and duct/system ` +
      `inputs are in ventilationInfiltration, internals and miscDetails. The house's walls, roofs, ` +
      `floors and partitions are arrays directly on the project. designTempInputs holds the design ` +
      `temperatures picked in the Results tab's Run Calculations dialog.\n` +
      `If 'fields' is omitted, ONLY the index is returned — no field data. Pass 'fields' to get actual data, ` +
      `e.g. ["/systems[0]", "/wallTypes"] or ["/walls", "/internals"] — either an indexed item from the ` +
      `'components' index, or a bare top-level section name to get all of it. To read the whole project, ` +
      `pass every name in 'sections'. Paths that don't exist in this project (e.g. "/spaces" in a Residential ` +
      `project) or are deeper than one level are skipped and returned in 'unknownPaths'. For a project you ` +
      `haven't read yet, an unscoped call first (to see what's there) followed by a scoped call for what you ` +
      `actually need is usually better than reading everything up front.`,
    inputSchema: {
      type: 'object',
      properties: {
        fields: {
          type: 'array',
          items: { type: 'string' },
          description: `Optional. Top-level paths to fetch data for, e.g. ["/systems[0]", "/wallTypes"] (Commercial) or ["/walls", "/internals"] (Residential). Omit to get only the 'components' index (no field data).`,
        },
      },
    },
    handle(msg) {
      const project = gApp.proj()
      const [snapshot] = FieldCollector.collect(project)
      const header = {
        projectType: project.getProjectType(),
        sections: getProjectSections(project),
        components: buildComponentsIndex(snapshot),
      }
      const fields = msg.input?.fields

      if (fields?.length) {
        const { userInputs, unknownPaths } = filterSnapshotToPaths(snapshot, fields)
        const result = { ...header, userInputs }
        if (unknownPaths.length) result.unknownPaths = unknownPaths
        const json = JSON.stringify(result, agentJsonReplacer)
        console.log(`Read project snapshot, scoped to [${fields.join(', ')}] (${json.length} chars)`)
        return json
      }

      console.log(`Read project snapshot: no 'fields' given, returning index only (${header.components.length} components)`)
      return JSON.stringify(header)
    },
  },
  {
    name: 'propose_changes',
    description: 'Propose changes to the project. Can modify field values or add/remove items from component arrays. The user will be shown the proposed changes and asked to accept or reject them before anything is applied. ' +
      "Paths must come from read_project for the current project — Commercial and Residential projects have different sections (e.g. a Residential project's walls are at '/walls[0]', a Commercial project's are inside a Space at '/spaces[0]/walls[0]'). " +
      "For change_type='add' on a component type you haven't seen a live example of in this project (e.g. an empty ComponentArray), read_agent_doc the matching file under component-schemas/ (see the component-schemas index; Residential projects use the component-schemas/residential/ docs) before constructing new_value, so field keys/types/choices are correct. " +
      "Some components (e.g. the project's location, at '/buildingAndEnv/locationData' in Commercial projects or '/toplevelData/locationData' in Residential ones) aren't plain fields — read_project shows a 'currentLocation' summary for these instead of a single value. To change one, use change_type='field' with new_value shaped as that component expects (for location: {\"path\": [...]}, using a path from the search_location tool — don't guess one). " +
"For a field of type 'TypeSelect' (e.g. a wall's Wall Type, a window's Window Type), which references an item in one of the type libraries (wallTypes, windowTypes, ...), new_value is that item's value from the field's choices, or its exact name — never a path like '/wallTypes[3]'. " +
      "For a field of type 'MultiTieredSelect' (e.g. Space Type, Appliance Type), new_value is a 'path' array. read_project doesn't inline choices for these (the full list can be hundreds of entries, and is the same for every instance) — read_agent_doc the component's schema under component-schemas/ instead, which lists every valid path. " +
      "To use a custom wall/roof material as a wall layer: first change_type='add' a new item at '/componentsLibrary/customMaterials' (fields: name, rValuePerInch, density), then read_project again — the created material's entry shows a 'typeId'. Set the layer's 'category' field to 'Custom Materials' and its 'type' field to that typeId. Some ComponentArray items (e.g. a WallBuilder layer's fixed first/last boundary layers, or a Schedule's built-in defaults) can't be removed or have output fields set directly — you'll get a clear error if you try. " +
      "Some numeric fields can be set in more than one unit (e.g. Summer Infiltration: cfm, cfm/ft² wall area, cfm/ft crack length, or ACH) — read_project includes a 'unitOptions' list of {value, label} for these. To set one in a unit other than what's currently shown, use new_value: {\"value\": 10, \"units\": \"<a unitOptions value>\"} instead of a plain number — don't just send the number in the wrong unit, and don't guess a units string that isn't in the list.",
    inputSchema: {
      type: 'object',
      properties: {
        changes: {
          type: 'array',
          description: 'List of changes to propose.',
          items: {
            type: 'object',
            properties: {
              change_type: {
                type: 'string',
                enum: ['field', 'add', 'remove'],
                description: "Type of change: 'field' to update a field value, 'add' to create a new item in a ComponentArray, 'remove' to delete an item from a ComponentArray.",
              },
              component_path: {
                type: 'string',
                description: "Path to the component to modify, as returned by read_project. For 'field': path to the field, e.g. '/spaces[0]/walls[0]/area' (Commercial) or '/walls[0]/area' (Residential). For 'add': path to the ComponentArray, e.g. '/systems' (Commercial) or '/walls' (Residential). For 'remove': path to the item, e.g. '/systems[0]' or '/walls[0]'.",
              },
              new_item_name: {
                type: 'string',
                description: "For change_type='add': name to give the newly created item. If omitted a name is generated automatically. Ignored for items that have no name field (e.g. a Residential project's walls, roofs, floors and partitions).",
              },
              new_value: {
                description: "For change_type='field': the new value for the field. For change_type='add': an optional object to initialize the new item, where keys are field names on the created object. Values may be nested — plain objects recurse into child objects, and arrays on ComponentArray fields will create and initialize new child items (e.g. { name: 'Main System', zones: [{ name: 'Zone 1' }] }).",
              },
            },
            required: ['change_type'],
          },
        },
      },
      required: ['changes'],
    },
    handle(msg, assistant) {
      const [, componentsMap, uiPathMap] = FieldCollector.collect(gApp.proj())
      const changes = msg.input.changes.map(change => {
        const uiPath = uiPathMap.get(change.component_path) ?? change.component_path
        if (change.change_type === 'field') {
          const field = componentsMap.get(change.component_path)
          const displayUnits = field instanceof Field ? field.getDisplayUnitsLabel() : null
          return { ...change, uiPath, displayUnits }
        }
        return { ...change, uiPath }
      })
      console.log(`Creating proposal block with changes:`, changes);
      assistant.chatBlocks.push(FieldChangesProposal.create({ changes }))
      return 'Proposal shown to the user for review. Their accept/reject decision will appear in a subsequent user message.'
    },
  },
  {
    name: 'get_project_errors',
    description:
      `Get the project's current errors (e.g. missing or invalid required fields), grouped by page. ` +
      `Empty if there are no known errors. Note: errors are computed as part of running a load ` +
      `calculation, so this reflects the state as of the last calculation attempt rather than being ` +
      `always perfectly live. In a Commercial project, run calculate_system first if you need a fresh ` +
      `check. In a Residential project, run calculate_house_loads, which also returns the errors directly.`,
    inputSchema: { type: 'object', properties: {} },
    handle() {
      const errors = sanitizeProjectErrors(gApp.proj().getProjectErrors().getErrors())
      console.log(`[tool] project errors:`, errors)
      return JSON.stringify(errors)
    },
  },
  {
    name: 'calculate_system',
    description:
      `Run the HVAC load calculation for a System and wait for it to finish (usually seconds, up to a ` +
      `few minutes for large projects). Returns the outcome: resultsOutcome on success, or the ` +
      `projectErrors / calculationError that stopped it. If it's still running after 5 minutes, it ` +
      `returns resultsState 'Calculating' instead; check again later with check_system_calculation_status. ` +
      `Does not return the actual loads; use get_system_results for those. ` + AFTER_CALCULATION_NOTE + ' ' +
      COMMERCIAL_ONLY_NOTE,
    inputSchema: {
      type: 'object',
      properties: {
        system_path: {
          type: 'string',
          description: "Path to the System to calculate, as returned by read_project or check_system_calculation_status, e.g. '/systems[0]'.",
        },
      },
      required: ['system_path'],
    },
    async handle(msg) {
      const system = resolveSystemFromPath(msg.input.system_path)
      console.log(`[tool] starting calculation for system '${system.getName()}' at ${msg.input.system_path}`)
      // Not awaited directly: if a calculation is already running this
      // returns right away, so wait on the results state instead.
      system.calculateLoadsAsync()
      const finished = await waitForSystemCalculation(system)
      const status = getSystemStatus(system, msg.input.system_path)
      if (!finished) {
        status.note = 'Still calculating after 5 minutes. Check again with check_system_calculation_status.'
      }
      console.log(`[tool] calculation for '${system.getName()}' finished:`, status)
      return JSON.stringify(status)
    },
  },
  {
    name: 'check_system_calculation_status',
    description:
      `Check the calculation status of every System in the project. For each System, reports whether it ` +
      `has no results yet, is currently calculating, or is ready — and if ready, whether the calculation ` +
      `succeeded or failed. If it failed due to project errors, those errors are included directly. If it ` +
      `failed due to a calculation error, that error message is included directly. Does not return the ` +
      `actual load results on success. ` + COMMERCIAL_ONLY_NOTE,
    inputSchema: { type: 'object', properties: {} },
    handle() {
      const systems = gApp.proj().systems
      if (!systems) {
        throw new Error(NO_SYSTEMS_ERROR)
      }
      const statuses = systems.map((system, i) => getSystemStatus(system, `/systems[${i}]`))
      console.log(`[tool] system calculation statuses:`, statuses)
      return JSON.stringify(statuses)
    },
  },
  {
    name: 'get_system_results',
    description:
      `Get the full load calculation results for a System that has finished calculating successfully ` +
      `(calculate_system returns the outcome; run it first if there are no results yet). Returns the same data as the Excel export ` +
      `(coil load summaries, space loads, system/space inputs, component tables, construction details), ` +
      `as plain-text CSV — one '# Sheet: <name>' section per worksheet. This can be a large amount of ` +
      `text, so only call it when you actually need the detailed results. ` + COMMERCIAL_ONLY_NOTE,
    inputSchema: {
      type: 'object',
      properties: {
        system_path: {
          type: 'string',
          description: "Path to the System to get results for, as returned by read_project or check_system_calculation_status, e.g. '/systems[0]'.",
        },
      },
      required: ['system_path'],
    },
    async handle(msg) {
      const system = resolveSystemFromPath(msg.input.system_path)
      if (!system.getResultsReadyAndSuccessful()) {
        throw new Error(`System '${system.getName()}' does not have successful results ready. Check its status with check_system_calculation_status first.`)
      }
      console.log(`[tool] getting results CSV for system '${system.getName()}' at ${msg.input.system_path}`)
      const csv = await getSystemResultsCsv(system)
      console.log(`[tool] results CSV for '${system.getName()}' is ${csv.length} chars`)
      return csv
    },
  },
  {
    name: 'calculate_house_loads',
    description:
      `Run the load calculation for a Residential project — the same as the Results tab's Run ` +
      `Calculations button, so the Results tab shows the new results too. Runs right away and returns ` +
      `the outcome: resultsOutcome 'Success', 'FailedWithProjectErrors' (with the projectErrors that ` +
      `stopped it — fix them and run again), or 'FailedWithCalculationError' (with the error message). ` +
      `Uses the heating/cooling design temperatures in designTempInputs (read_project "/designTempInputs"; ` +
      `the user normally picks these in the Run Calculations dialog). Results don't update on their own ` +
      `when inputs change, so run this again after changing inputs. Does not return the actual loads — ` +
      `use get_house_results for those. ` + AFTER_CALCULATION_NOTE + ' ' + RESIDENTIAL_ONLY_NOTE,
    inputSchema: { type: 'object', properties: {} },
    handle() {
      const project = requireResidentialProject()
      console.log(`[tool] running residential load calculation`)
      project.updateResults()
      const status = getHouseResultsStatus(project)
      console.log(`[tool] residential calculation status:`, status)
      return JSON.stringify(status)
    },
  },
  {
    name: 'get_house_results',
    description:
      `Get the full load calculation results for a Residential project, from its last successful ` +
      `calculation (run calculate_house_loads first if there are none, or if inputs changed since). ` +
      `Returns plain-text CSV, one '# Sheet: <name>' section per table: Info, Summary (total cooling/` +
      `heating loads, design temperatures, floor area), Load Breakdown (envelope, outdoor air, internal ` +
      `and final loads), Component Breakdown (each wall, window, door, roof, etc.), and Inputs Summary. ` +
      `Values are in the project's display units. ` + RESIDENTIAL_ONLY_NOTE,
    inputSchema: { type: 'object', properties: {} },
    handle() {
      const project = requireResidentialProject()
      const status = getHouseResultsStatus(project)
      if (status.resultsOutcome !== 'Success') {
        throw new Error(`There are no successful results to read. Current status: ${JSON.stringify(status)}. Run calculate_house_loads (after fixing any project errors).`)
      }
      const csv = getResidentialResultsCsv(project.getResults())
      console.log(`[tool] residential results CSV is ${csv.length} chars`)
      return csv
    },
  },
  {
    name: 'search_location',
    description:
      `Search the location/weather-station database for a location matching the given text ` +
      `(e.g. a city, state/province, or region name). Returns up to 20 matches, each as ` +
      `{fullName, name, path}. Use the 'path' from a result as the new_value when setting a ` +
      `project's location via propose_changes (change_type='field', new_value: {"path": [...]}) — ` +
      `don't guess a path, it must come from this tool. If nothing matches, try a broader or ` +
      `differently-spelled query.`,
    inputSchema: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: `Text to search for, matched case-insensitively against each location's full name, e.g. "Chicago" or "Ontario".`,
        },
      },
      required: ['query'],
    },
    handle(msg) {
      const results = searchLocations(msg.input.query)
      console.log(`[tool] search_location('${msg.input.query}') -> ${results.length} results`)
      return JSON.stringify(results)
    },
  },
]
